import { DefaultGameState, GameState } from "@shared/types";
import { Page } from "../enums/Page";
import { css, html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { KeypadService } from "../services/KeypadService";

const styles: string = css`
   :host {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .keypad {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        background-color: #333;
        padding: 15px;
        border-radius: 10px;
        box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
    }

    .keypadDisplay {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 10px;
        width: 100%;
        background-color: black;
        color: limegreen;
        font-size: 24px;
        font-family: monospace;
        border-radius: 5px;
        border: 2px solid limegreen;
    }

    .keypadDisplay div {
        height: 30px;
        text-align: center;
    }

    .numpad {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
    }

    .numpad div {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .numpad button {
        width: 50px;
        height: 50px;
        font-size: 18px;
        font-weight: bold;
        background-color: #444;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
    }

    .numpad button:hover {
        background-color: #666;
        transform: scale(1.1);
    }

    .numpad button:active {
        background-color: #222;
    }
`;

export class MiniKeypadComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private _currentGameState?: DefaultGameState;

    private _gameEventService: GameEventService = new GameEventService();
    private _keypadService: KeypadService = new KeypadService();

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this._currentGameState = state as DefaultGameState;
        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const element: HTMLElement = this.renderKeypad();

        this.shadowRoot.firstChild?.remove();
        this.shadowRoot.append(element);
    }

    private renderKeypad(): HTMLElement {
        const element: HTMLElement = html`
        <div>
            <style>
                ${styles}
            </style>

            <div class="keypad">
                <div class="keypadDisplay">
                    <div>.</div>
                    <div>.</div>
                    <div>.</div>
                    <div>.</div>
                </div>

                <div class="numpad">
                    <div><button>7</button></div>
                    <div><button>8</button></div>
                    <div><button>9</button></div>
                    <div><button>4</button></div>
                    <div><button>5</button></div>
                    <div><button>6</button></div>
                    <div><button>1</button></div>
                    <div><button>2</button></div>
                    <div><button>3</button></div>
                    <div><button>X</button></div>
                    <div><button>&lt;</button></div>
                    <div><button>&#10003;</button></div>
                </div>
            </div>
        </div>
        `;

        const keypadDisplay: NodeListOf<HTMLElement> = element.querySelectorAll(".keypadDisplay > div");

        let currentKey: number = 0;

        element.querySelectorAll("button").forEach(e => {
            const key: string = e.innerText;

            e.addEventListener("click", async () => {
                if (key === "<") {
                    currentKey--;

                    if (currentKey < 0) {
                        currentKey = 0;
                    }

                    keypadDisplay[currentKey].innerText = ".";
                }
                else if (key === "X") {
                    this._gameEventService.switchPage(Page.Canvas);
                }
                else if (key.charCodeAt(0) === 10003) { // 10003 = check icon
                    if (currentKey < 4) {
                        return;
                    }

                    keypadDisplay.forEach(f => f.style.display = "none");

                    const enteredCode: string = Array.from(keypadDisplay)
                        .map(div => div.innerText)
                        .join(""); // converts to string

                    const alias: string = this._currentGameState?.roomAlias as string;
                    const isCodeCorrect: boolean = await this._keypadService.checkCode(enteredCode, alias);

                    if (isCodeCorrect) {
                        keypadDisplay[0].style.display = "block";
                        keypadDisplay[0].innerText = "OK";
                        setTimeout(() => {
                            this._gameEventService.switchPage(Page.Canvas);
                        }, 500);
                    }
                    else {
                        keypadDisplay[0].style.display = "block";
                        keypadDisplay[0].innerText = "ERROR";
                    }
                }
                else {
                    if (currentKey > 3) {
                        return;
                    }

                    keypadDisplay[currentKey].innerText = key;

                    currentKey++;
                }
            });
        });

        return element;
    }
}
